import { useState, useEffect, useCallback } from 'react';

interface UseTriageTimerOptions {
  initialSeconds?: number;
  revealed: boolean;
  onExpire: () => void;
}

export function useTriageTimer({
  initialSeconds = 180,
  revealed,
  onExpire,
}: UseTriageTimerOptions) {
  const [secondsLeft, setSecondsLeft] = useState<number>(initialSeconds);
  const [timerActive, setTimerActive] = useState<boolean>(false);

  // Reset timer
  const resetTimer = useCallback(() => {
    setSecondsLeft(initialSeconds);
    setTimerActive(false);
  }, [initialSeconds]);

  // Countdown effect
  useEffect(() => {
    if (!timerActive || revealed || secondsLeft <= 0) return;
    const interval = setInterval(() => {
      setSecondsLeft((s) => s - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timerActive, secondsLeft, revealed]);

  // Expiration effect
  useEffect(() => {
    if (secondsLeft === 0 && !revealed) {
      onExpire();
    }
  }, [secondsLeft, revealed, onExpire]);

  // Format mm:ss helper
  const formattedTime = `${Math.floor(secondsLeft / 60)
    .toString()
    .padStart(2, '0')}:${(secondsLeft % 60).toString().padStart(2, '0')}`;

  return {
    secondsLeft,
    timerActive,
    setTimerActive,
    resetTimer,
    formattedTime,
  };
}
